/**
 * useMountedRef — track whether the calling component is still
 * mounted, so async callbacks can gate state writes on a fresh check
 * without re-creating the closure on every render.
 *
 * Story H1. Used by `useApproveFlow`, `useReviseFlow`, and
 * `WorkflowV2.chainedOnApprove` to avoid "Can't perform a React state
 * update on an unmounted component" warnings. The pattern is:
 *
 *   const mountedRef = useMountedRef();
 *   ...
 *   setTimeout(() => {
 *     if (!mountedRef.current) return;
 *     setSomething(false);
 *   }, 500);
 *
 * The returned object is a stable ref — never re-assigned — so it's
 * safe to use as a stable dependency / read inside long-lived
 * callbacks.
 */
import { useEffect, useRef } from 'react';

export function useMountedRef(): { readonly current: boolean } {
  const ref = useRef(true);
  useEffect(() => {
    ref.current = true;
    return () => {
      ref.current = false;
    };
  }, []);
  return ref;
}
